import { Plus, ArrowsClockwise, TrendUp, TrendDown, Lightning } from "@phosphor-icons/react";

/**
 * Replacement for the bottom "+ Add Week" button. Lets the user pick what
 * KIND of next week to spin up:
 *   • Empty   — blank slate (no API)
 *   • Repeat  — verbatim copy of the last week (week_type=repeat)
 *   • Progress — +5% weight / +2 reps (week_type=progress)
 *   • Deload  — -10% weight, -1 set, plyo cut to 1×3 (week_type=deload)
 *   • Peak    — +5% weight + drop reps to 3, plyo trimmed to 2×3 (week_type=peak)
 *
 * The non-Empty options call the backend `/plans/{id}/progress-week` endpoint
 * with the appropriate `week_type` and require the plan to be saved first.
 */
export default function AddWeekMenu({ plan, onAddEmpty, onProgressWeek, isSaved }) {
  const lastWeek = plan?.weeks?.[plan.weeks.length - 1];
  const lastIdx = lastWeek?.week_index;
  const hasLastWeekContent = !!lastWeek?.days?.some((d) => d.exercises && d.exercises.length > 0);

  const transformOpts = [
    {
      id: "repeat",
      label: "Repeat",
      sub: "Same as last week",
      icon: ArrowsClockwise,
      onClick: () => onProgressWeek(lastIdx, "repeat"),
    },
    {
      id: "progress",
      label: "Progress",
      sub: "+5% / +2 reps",
      icon: TrendUp,
      onClick: () => onProgressWeek(lastIdx, "progress"),
      accent: true,
    },
    {
      id: "deload",
      label: "Deload",
      sub: "-10%, -1 set, plyo cut",
      icon: TrendDown,
      onClick: () => onProgressWeek(lastIdx, "deload"),
    },
    {
      id: "peak",
      label: "Peak",
      sub: "+5% + heavy triples",
      icon: Lightning,
      onClick: () => onProgressWeek(lastIdx, "peak"),
    },
  ];

  const disabledTransforms = !isSaved || !hasLastWeekContent;
  const disabledTitle = !isSaved
    ? "Save the plan first to spin up an auto-tuned week"
    : !hasLastWeekContent
      ? "Add at least one exercise to the last week first"
      : "";

  return (
    <div className="border-2 border-dashed border-border p-4" data-testid="add-week-menu">
      <div className="label-mono text-muted-foreground mb-3">// ADD NEXT WEEK</div>
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
        <button
          onClick={onAddEmpty}
          className="border border-border px-3 py-3 text-left hover:border-volt hover:bg-volt/5 transition-colors group"
          data-testid="add-week-empty"
        >
          <div className="flex items-center gap-2 font-display font-bold uppercase tracking-widest text-sm group-hover:text-volt">
            <Plus size={16} weight="bold" />
            Empty
          </div>
          <div className="text-xs text-muted-foreground mt-1">Build from scratch</div>
        </button>
        {transformOpts.map((o) => {
          const Icon = o.icon;
          return (
            <button
              key={o.id}
              onClick={o.onClick}
              disabled={disabledTransforms}
              title={disabledTransforms ? disabledTitle : `Spin up a ${o.label.toLowerCase()} week from week ${lastIdx}`}
              className={`border px-3 py-3 text-left transition-colors group disabled:opacity-40 disabled:cursor-not-allowed ${
                o.accent
                  ? "border-volt text-volt hover:bg-volt hover:text-black"
                  : "border-border hover:border-volt hover:bg-volt/5"
              }`}
              data-testid={`add-week-${o.id}`}
            >
              <div className={`flex items-center gap-2 font-display font-bold uppercase tracking-widest text-sm ${o.accent ? "" : "group-hover:text-volt"}`}>
                <Icon size={16} weight="bold" />
                {o.label}
              </div>
              <div className={`text-xs mt-1 ${o.accent ? "text-volt/80 group-hover:text-black/70" : "text-muted-foreground"}`}>
                {o.sub}
              </div>
            </button>
          );
        })}
      </div>
      {!isSaved && (
        <div className="text-xs text-muted-foreground mt-3 label-mono">
          // SAVE THE PLAN TO UNLOCK PROGRESS / DELOAD / PEAK
        </div>
      )}
    </div>
  );
}
